import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { ExtraGuestSettingsFormattedValue } from 'app/components/pages/Extranet/commons/ExtraGuest/ExtraGuest';
import { TaskName } from 'app/components/pages/Extranet/commons/TaskCenter/Legacy/Types';
import type { GuestTask } from 'app/components/pages/Extranet/commons/TaskCenter/Legacy/Types';
import type { IAdditionnalPax } from 'app/typings/additionalPax';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const path = '/hotel-admin/:hotelId/package' as const;
const pathSavePaxBase = '/extranet/:hotelId/packages' as const;

const getTaskPath = (taskName?: GuestTask) => {
  switch (taskName) {
    case TaskName.ConfigureChildPrice:
      return 'extra-child-fees';
    case TaskName.AttractExtraGuests:
      return 'extra-guest-fees';
    case TaskName.AttractSingleGuests:
      return 'single-guest-discounts';
  }
};

const getTaskSuccessMessage = (taskName?: GuestTask) => {
  switch (taskName) {
    case TaskName.ConfigureChildPrice:
      return 'Successfully saved additional price for children';
    case TaskName.AttractExtraGuests:
      return 'Successfully saved additional PAX';
    case TaskName.AttractSingleGuests:
      return 'Successfully saved single guest discount';
  }
};

export function useAdditionalPax(hotelId: number) {
  return useQuery({
    queryKey: [path, { hotelId }],
    queryFn: () =>
      apiRequest({
        path,
        params: { hotelId: hotelId.toString() },
      }) as Promise<IAdditionnalPax>,
    staleTime: Infinity,
  });
}

export function useSaveAdditionalPax(hotelId: number, taskName?: GuestTask) {
  const taskPath = getTaskPath(taskName);

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      settings: ExtraGuestSettingsFormattedValue[];
    }) =>
      apiMutation(
        'POST',
        {
          path: `${pathSavePaxBase}/${taskPath}`,
          params: {
            hotelId: hotelId.toString(),
          },
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries();
      message.success(getTaskSuccessMessage(taskName));
    },
    onError: () => {
      message.error('Error saving additional PAX');
    },
  });
}
